import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
} from 'react';
import {
  IAuditionFormData,
  IContactInfo,
} from '@type/audition/reservation.type';
import { t } from 'utils/i18n';
import { TRIAL_COURSE_TEACHER_GENDER } from 'api/mms/backend/course-claim/course-claim.enum';
export type TInputError = {
  teacher: string;
  examTags: string;
  homeworkTags: string;
  courseReminder: string;
  interactiveLearningTags: string;
};
interface IReservationCourse {
  isOneClubExistError: boolean;
  infoIsGot: boolean;
  contactError: string;
  contactInfo: IContactInfo | null;
  setContactInfo: Dispatch<SetStateAction<IContactInfo | null>>;
  inputError: TInputError;
  formData: IAuditionFormData;
  setFormData: Dispatch<SetStateAction<IAuditionFormData>>;
  initState: () => void;
  initFormData: () => void;
  initInputError: () => void;
  removeTeacherInfo: () => void;
  removeResourceInfo: () => void;
  handleInputError: (key: keyof TInputError, value: string) => void;
  handleOneClubExistError: (isError: boolean) => void;
  handleInfoIsGot: (isGot: boolean) => void;
  handleContactError: (value: string) => void;
}

const useReservationCourse = (): IReservationCourse => {
  const [isOneClubExistError, setIsOneClubExistError] = useState(false);
  const [infoIsGot, setInfoIsGot] = useState(false);
  const [contactInfo, setContactInfo] = useState<IContactInfo | null>(null);
  const [contactError, setContactError] = useState('');
  const [inputError, setInputError] = useState<TInputError>({
    teacher: t(
      'audition.reservation.components.reservationAddDialog.fieldRequired',
      '此欄位必填',
    ),
    examTags: '',
    homeworkTags: '',
    courseReminder: t(
      'audition.reservation.components.reservationAddDialog.fieldRequired',
      '此欄位必填',
    ),
    interactiveLearningTags: '',
  });
  // customer 存在的話，代表在 mms 找出來的會員
  const [formData, setFormData] = useState<IAuditionFormData>({
    contactId: '',
    oneClubId: '',
    phone: {
      countryCode: '',
      phoneNumber: '',
    },
    studentInfo: {
      isNew: true,
      id: '',
      name: '',
      education: '',
      grade: 1,
      groupId: '',
    },
    resourceInfo: {
      teacher: null,
      teacherGender: TRIAL_COURSE_TEACHER_GENDER.BOTH,
      seriesTags: [],
      resourceTags: [],
      examTags: [],
      interactiveLearningTags: [],
    },
    isMmsCustomer: false,
  });
  const initFormData = () => {
    setFormData(() => ({
      contactId: '',
      phone: {
        countryCode: '',
        phoneNumber: '',
      },
      oneClubId: '',
      studentInfo: {
        isNew: true,
        id: '',
        name: '',
        education: '',
        grade: 1,
        groupId: '',
      },
      resourceInfo: {
        teacher: null,
        seriesTags: [],
        resourceTags: [],
        examTags: [],
        teacherGender: TRIAL_COURSE_TEACHER_GENDER.BOTH,
        interactiveLearningTags: [],
      },
      isMmsCustomer: false,
    }));
  };
  const initInputError = () => {
    setInputError(() => ({
      teacher: t(
        'audition.reservation.components.reservationAddDialog.fieldRequired',
        '此欄位必填',
      ),
      examTags: '',
      homeworkTags: '',
      courseReminder: t(
        'audition.reservation.components.reservationAddDialog.fieldRequired',
        '此欄位必填',
      ),
      interactiveLearningTags: '',
    }));
  };
  const initState = () => {
    setContactInfo(null);
    setInfoIsGot(false);
    setIsOneClubExistError(false);
    initInputError();
    initFormData();
  };

  const removeTeacherInfo = () => {
    setFormData(prev => ({
      ...prev,
      resourceInfo: {
        ...prev.resourceInfo,
        teacher: null,
      },
    }));
    setInputError(prev => ({
      ...prev,
      teacher: t(
        'audition.reservation.components.reservationAddDialog.fieldRequired',
        '此欄位必填',
      ),
    }));
  };

  const removeResourceInfo = () => {
    setFormData(prev => ({
      ...prev,
      resourceInfo: {
        ...prev.resourceInfo,
        seriesTags: [],
        examTags: [],
        resourceTags: [],
        homeworkTags: [],
        interactiveLearningTags: [],
      },
    }));
    setInputError(prev => ({
      ...prev,
      examTags: '',
      homeworkTags: '',
    }));
  };

  const handleInputError = (key: keyof TInputError, value: string) => {
    setInputError(prev => ({
      ...prev,
      [key]: value,
    }));
  };
  const handleOneClubExistError = (isError: boolean) => {
    setIsOneClubExistError(isError);
  };
  const handleInfoIsGot = (isGot: boolean) => {
    setInfoIsGot(isGot);
  };
  const handleContactError = (value: string) => {
    setContactError(value);
  };

  return {
    isOneClubExistError,
    infoIsGot,
    contactError,
    contactInfo,
    inputError,
    formData,
    initState,
    initFormData,
    initInputError,
    removeTeacherInfo,
    removeResourceInfo,
    handleInputError,
    handleOneClubExistError,
    handleInfoIsGot,
    handleContactError,
    setContactInfo,
    setFormData,
  };
};

function createService<T>(
  func: (...args: any[]) => T,
  initValue: T | undefined = undefined,
) {
  return createContext(initValue as T);
}
export const ReservationCourseService = createService(useReservationCourse);
export const ReservationCourseProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <ReservationCourseService.Provider value={useReservationCourse()}>
      {children}
    </ReservationCourseService.Provider>
  );
};

export const useReservationCourseService = () =>
  useContext(ReservationCourseService);
